<template>
  <div
    :class="isMobile ? 'image-wrapper--mobile' : 'image-wrapper'"
    :style="wrapperStyle"
  >
    <image-field
      :src="resolvedImage"
      :style="imageStyle"
      :contain="contain"
      :class="isImageResolved ? '' : 'invalid-image'"
    />
    <!-- Pencil icon overlay for image upload -->
    <div
      v-if="edit || createMode"
      class="pencil-icon"
      :style="pencilStyle"
      @dblclick="onDblClickUnlockEdit"
    >
      <image-field
        src="/img/icons/edit-pencil-icon.svg"
        contain
        max-width="32"
        height="32"
        @onClick="triggerImageUpload"
      />
    </div>

    <!-- Clears the picked/stored image. Whether that is only a local reset or
         also reaches the backend is the caller's business - this reports it. -->
    <v-icon
      v-if="canDeleteImage"
      class="delete-icon"
      :style="deleteStyle"
      :ripple="false"
      @click="$emit('delete-image')"
    >
      mdi-delete-outline
    </v-icon>

    <!-- Hidden file input for uploading image -->
    <input
      v-if="showUploadButton"
      ref="fileInput"
      type="file"
      class="d-none"
      :accept="'image/*'"
      enctype="multipart/form-data"
      @change="handleImageUpload"
    >
  </div>
</template>

<script>
import { notify } from '@/global/services/notification/notificationBus'
import ImageField from '@/global/components/fields/ImageField.vue'
import { resolveImage } from '@/global/services/helpers/images'
import { convertToBase64 } from '@/global/services/helpers/files'
import { isArray, isSet, isString, forEach, getBooleanFromValue } from '@/global/utils/utils'
import helperMixin from '@/global/mixins/helperMixin'

const DEFAULT_NOT_FOUND_IMAGE = '/img/default_images/image-not-found.png'
export default {
  name: 'ImageUpload',

  components: {
    ImageField
  },

  mixins: [
    helperMixin
  ],

  props: {
    imageStyle: {
      type: Object,
      default: () => ({})
    },
    src: {
      type: [String, Array, File, Function],
      default: null
    },
    contain: {
      type: Boolean,
      default: false
    },
    fieldKey: {
      type: [Object, String],
      default: null
    },
    edit: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    },
    showUploadButton: {
      type: Boolean,
      default: true
    },
    // Off by default: the small avatars this component was built for are replaced
    // rather than cleared, and they have nowhere to put a second icon.
    showDeleteButton: {
      type: Boolean,
      default: false
    },
    wrapperStyle: {
      type: Object,
      default: () => ({})
    },
    // Nudges the upload pencil away from the image's bottom-right corner. Left
    // empty on purpose: the small round avatars this component was built for
    // (partner logo, person photo) deliberately sit the pencil flush in the
    // corner, and insetting it there would push it onto the image itself. Large
    // photo panels pass their own inset, eg. `{ bottom: '12px', right: '12px' }`.
    pencilStyle: {
      type: Object,
      default: () => ({})
    },
    // Same idea as `pencilStyle`, for the delete icon sitting next to it.
    deleteStyle: {
      type: Object,
      default: () => ({})
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    updateValidationErrors: {
      type: Function,
      default: () => {}
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      resolvedImage: DEFAULT_NOT_FOUND_IMAGE,
      isImageResolved: false
    }
  },

  computed: {
    configDisabled () {
      return getBooleanFromValue(this.config?.disabled)
    },

    canDeleteImage () {
      return this.showDeleteButton &&
        (this.edit || this.createMode) &&
        !this.configDisabled &&
        !!this.src
    }
  },

  watch: {
    src: {
      immediate: true,
      async handler (newSrc) {
        // Emptied rather than replaced - a cleared image has to leave the panel,
        // not stay on it as the last one that resolved.
        if (!newSrc) {
          this.resolvedImage = DEFAULT_NOT_FOUND_IMAGE
          this.isImageResolved = false
          return
        }

        if (newSrc instanceof File) {
          this.resolvedImage = await convertToBase64(newSrc)
          return
        }

        if (isArray(newSrc) && newSrc.length) {
          this.resolvedImage = newSrc[0]
          return
        }

        const isDataOrBlob = /^data:image\/[a-zA-Z]+;base64,/.test(newSrc) || (isString(newSrc) && newSrc.startsWith('blob:'))
        if (isDataOrBlob) {
          this.resolvedImage = newSrc
          return
        }

        if (isString(newSrc)) {
          const resolved = await resolveImage(newSrc)
          this.isImageResolved = !!resolved
          this.resolvedImage = resolved || DEFAULT_NOT_FOUND_IMAGE
        }
      }
    }
  },

  methods: {
    onDblClickUnlockEdit () {
      if (this.configDisabled) {
        return
      }

      this.$emit('unlock-fields-on-dbl-click')
    },

    triggerImageUpload () {
      if (!isSet(this.$refs?.fileInput)) {
        return
      }

      this.$refs.fileInput.value = ''
      this.$refs.fileInput.click()
    },

    handleImageUpload (event) {
      const uploadedImages = event.target.files
      const maxUploadImages = 1
      let invalidMessage = ''

      // Checking whether the number of uploaded files exceeds the limit
      if (uploadedImages.length > maxUploadImages) {
        notify(this.$t('fleet/vehicle.maximum_images_uploaded').replace(':max-images', maxUploadImages))
        return
      }

      // Checks are uploaded files have correct formats (only images are allowed)
      forEach(uploadedImages, uploadedImage => {
        if (!uploadedImage.type.startsWith('image/')) {
          invalidMessage += this.$t('fleet/vehicle.not_valid_image_format_message').replace(':image', uploadedImage.name)
        }
      })

      if (!invalidMessage && this.fieldKey) {
        forEach(uploadedImages, uploadedImage => {
          this.updateData({
            [this.fieldKey]: uploadedImage
          })
        })
      }
      else {
        notify(invalidMessage)
      }
    }
  }
}
</script>

<style scoped>
.image-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}

.image-wrapper--mobile {
  position: relative;
  height: 100%;
}

.pencil-icon {
  position: absolute;
  bottom: 0;
  right: 0;
  cursor: pointer;
}

.delete-icon {
  position: absolute;
  /* Left of where the pencil sits, so the two do not stack on each other. */
  bottom: 0;
  right: 40px;
  color: #EF4444;
  background: #F3F4F6;
  font-size: 21px;
  border-radius: 50%;
  padding: 5px;
  cursor: pointer;
}

.delete-icon:after {
  opacity: 0 !important;
}

.invalid-image {
  border: 1px solid #E5E7EB;
}
</style>
